import styled from 'styled-components'
import useAction from '~/prix/react/hooks/action'
import patchNotesDefinition from '../generalPatchNotes.definition'
import Handle from '~/prix/react/components/handle'
import { GenericObject } from '~/prix/types/type'
import Header from '~/components/card/header'
import { PatchNotesEnum } from '../generalPatchNotesObject'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (max-width: 870px) {
    padding: 30px 0;
  }
`

const Container = styled.main`
  padding: ${({ theme }) => theme.padding(2)};
  justify-content: end;
  gap: ${({ theme }) => theme.padding(2)};
  background-color: #fff;
  margin-bottom: 1rem;
  border-bottom-left-radius: ${({ theme }) => theme.borderRadius};
  border-bottom-right-radius: ${({ theme }) => theme.borderRadius};
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
`

const Item = styled.div`
  display: flex;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    display: flex;
    flex-direction: column;
  }
  li {
    list-style: none;
  }

  h2 {
    font-size: 18px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.padding(1)};
    margin: 0;
  }

  h3 {
    font-size: 14px;
    font-weight: 400;
  }

  .tag {
    background-color: ${({ theme }) => theme.colors.primary};
    width: fit-content;
    padding: ${({ theme }) => theme.padding(1)} ${({ theme }) => theme.padding(2)};
    border-radius: 8px;
    color: #fff;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
  }
`

const TitleMonth = styled.h2`
  width: fit-content;
  text-transform: capitalize;
  margin-bottom: 20px;
  background-color: ${({ theme }) => theme.colors.lightGrey};
  padding: ${({ theme }) => theme.padding(1)} ${({ theme }) => theme.padding(2)};
  border-radius: ${({ theme }) => theme.borderRadius};
`

const DateBadge = styled.span`
  background-color: ${({ theme }) => theme.colors.mediumGrey};
  color: ${({ theme }) => theme.colors.primary};
  padding: ${({ theme }) => theme.padding(1)} ${({ theme }) => theme.padding(2)};
  border-radius: ${({ theme }) => theme.borderRadius};
  font-size: 14px;
  font-weight: 500;
  margin-left: ${({ theme }) => theme.padding(2)};
`

const InformationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.padding(1)};
  margin-top: ${({ theme }) => theme.padding(1)};
  margin-bottom: ${({ theme }) => theme.padding(2)};
`

const DescriptionField = styled.h3`
  font-size: 14px;
  font-weight: 400;
`

const Tag = styled.h4`
  background-color: ${({ theme }) => theme.colors.primary};
  width: fit-content;
  padding: ${({ theme }) => theme.padding(1)} ${({ theme }) => theme.padding(2)};
  margin: 0;
  border-radius: 8px;
  color: #fff;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
`

const MoreInformationField = styled.ul`
  list-style: none;
  margin: 0;

  li {
    margin-top: ${({ theme }) => theme.padding(1)};
  }
`

const formatDescription = (item: GenericObject) => {
  const { patchDescription, patchDate, patchVersion, patchName, ...sections } = item

  return (
    <>
      {patchDescription && <DescriptionField>{patchDescription as string}</DescriptionField>}

      {Object.entries(sections).map(([sectionKey, sectionData]) => {
        if (!Array.isArray(sectionData) || sectionData.length === 0) return null

        const formattedContent = sectionData
          .map(itemData => {
            const entries = Object.entries(itemData)
            const descriptionField = entries.find(([key]) => key.endsWith('Description'))
            const nameField = entries.find(([key]) => key.endsWith('Name'))

            if (nameField && descriptionField) {
              return `${nameField[1]}: ${descriptionField[1]}`
            } else if (descriptionField) {
              return descriptionField[1]
            }
            return ''
          })
          .filter(Boolean)

        if (formattedContent.length === 0) return null

        return (
          <InformationContainer key={sectionKey}>
            <Tag>
              {PatchNotesEnum[sectionKey as keyof typeof PatchNotesEnum]?.label || sectionKey}
            </Tag>
            <MoreInformationField>
              {formattedContent.map((content, index) => (
                <li key={index}>- {content as string}</li>
              ))}
            </MoreInformationField>
          </InformationContainer>
        )
      })}
    </>
  )
}

const PatchNotesScreen = () => {
  const patchNotesData = useAction(
    patchNotesDefinition,
    {
      type: 'prixGov',
    },
    [],
    {
      autoLoad: true,
      staleWhileRevalidate: false,
    },
  )
  const { result, isLoading } = patchNotesData

  return (
    <Handle
      isLoading={isLoading}
      style={{
        height: '100%',
      }}
    >
      <Wrapper>
        <Header title='Notas de atualização' />
        <Container>
          {Object.entries(result?.items || {}).map(([monthYear, items]) => (
            <div key={monthYear}>
              <TitleMonth>
                {monthYear.split('-')[0]} {monthYear.split('-')[1]}
              </TitleMonth>
              {items.map((item: GenericObject) => (
                <Item key={item.patchDate as string}>
                  <div>
                    <h2>
                      {item.patchVersion as string} {item.patchName ? `- ${item.patchName}` : ''}
                      <DateBadge>{item.patchDate as string}</DateBadge>
                    </h2>
                    {formatDescription(item)}
                  </div>
                </Item>
              ))}
            </div>
          ))}
          {Object.entries(result?.items || {}).length === 0 && (
            <p>Nenhuma nota de atualização encontrada</p>
          )}
        </Container>
      </Wrapper>
    </Handle>
  )
}

export default PatchNotesScreen
